import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Mail } from "lucide-react";

const EMAIL = "getcvaipro@gmail.com";

export const Footer = () => {
  return (
    <footer
      className="relative border-t border-white/5 bg-black"
      data-testid="site-footer"
    >
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 -top-px h-px w-2/3 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"
      />

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand + support message */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </span>
              <span className="text-sm font-semibold tracking-tight text-white">
                CV AI <span className="text-zinc-400 font-medium">PRO</span>
              </span>
            </div>
            <p className="mt-4 max-w-sm text-sm text-zinc-400 leading-relaxed">
              Need help or have questions? Contact us anytime.
            </p>
            <a
              href={`mailto:${EMAIL}`}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors"
              data-testid="footer-email-link"
            >
              <Mail className="h-4 w-4 text-zinc-300" />
              {EMAIL}
            </a>
          </div>

          {/* Navigate */}
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Navigate
            </p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <a
                  href="#examples"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-examples"
                >
                  Examples
                </a>
              </li>
              <li>
                <a
                  href="#benefits"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-benefits"
                >
                  Benefits
                </a>
              </li>
              <li>
                <a
                  href="#pricing"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-pricing"
                >
                  Pricing
                </a>
              </li>
              <li>
                <a
                  href="#faq"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-faq"
                >
                  FAQ
                </a>
              </li>
              <li>
                <Link
                  to="/resume-writing-service"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-rws"
                >
                  Resume Writing Service
                </Link>
              </li>
              <li>
                <Link
                  to="/ai-resume-builder"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-arb"
                >
                  AI Resume Builder
                </Link>
              </li>
              <li>
                <Link
                  to="/free-ats-checker"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-ats"
                >
                  Free ATS Checker
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-terms"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/refund"
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-link-refund-policy"
                >
                  Refund Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Support
            </p>
            <div className="mt-4 space-y-5 text-sm">
              <div data-testid="support-email-block">
                <p className="text-zinc-500 text-xs">Email Support</p>
                <a
                  href={`mailto:${EMAIL}`}
                  className="group mt-1.5 inline-flex items-center gap-1.5 text-zinc-200 hover:text-white transition-colors"
                  data-testid="footer-link-support"
                >
                  <Mail className="h-3.5 w-3.5 text-zinc-400 group-hover:text-white transition-colors" />
                  <span className="relative">
                    {EMAIL}
                    <span
                      aria-hidden
                      className="absolute -bottom-0.5 left-0 h-px w-0 bg-white group-hover:w-full transition-all duration-300"
                    />
                  </span>
                </a>
              </div>

              <div data-testid="support-refunds-block">
                <p className="text-zinc-500 text-xs">Refunds</p>
                <p className="mt-1.5 text-zinc-400 leading-relaxed">
                  No refunds — all sales are final. Questions?{" "}
                  <a
                    href={`mailto:${EMAIL}?subject=Support`}
                    className="text-zinc-200 hover:text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white transition-colors"
                    data-testid="footer-link-refund"
                  >
                    Contact support
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <span>© {new Date().getFullYear()} CV AI PRO. All rights reserved.</span>
          <a
            href={`mailto:${EMAIL}`}
            className="hover:text-zinc-300 transition-colors"
            data-testid="footer-bottom-email"
          >
            {EMAIL}
          </a>
        </div>
      </div>
    </footer>
  );
};
